import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Alert,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';

import {
    getFullImageUrl,
    getThumbnailUrl,
} from "helpers/utils";
import CustomSectionCreator from "components/CustomSectionCreator";

const ModelTryOnSelector = ({
    open,
    onClose,
    onSelectModel,
    models,
    loading,
    loadingMore,
    hasMore,
    onLoadMore,
    generating,
    genderFilter = '',
    onGenderFilterChange,
}) => {
    const [selectedModelId, setSelectedModelId] = useState(null);
    const contentRef = useRef(null);
    const sentinelRef = useRef(null);
    const onLoadMoreRef = useRef(onLoadMore);

    useEffect(() => {
        onLoadMoreRef.current = onLoadMore;
    }, [onLoadMore]);

    useEffect(() => {
        if (!open) setSelectedModelId(null);
    }, [open]);

    const handleSelectModel = (model) => {
        setSelectedModelId(model._id);
    };

    const handleConfirm = () => {
        if (selectedModelId && onSelectModel) {
            onSelectModel(selectedModelId);
        }
    };

    const handleAutoSelect = () => {
        if (onSelectModel) {
            onSelectModel(null);
        }
    };

    const showListLoading = loading && (!models || models.length === 0);

    const tryLoadMore = useCallback(() => {
        if (!hasMore || loading || loadingMore) return;
        if (typeof onLoadMoreRef.current === 'function') {
            onLoadMoreRef.current();
        }
    }, [hasMore, loading, loadingMore]);

    useEffect(() => {
        if (!open || !hasMore || loading || loadingMore) return;
        const root = contentRef.current;
        const target = sentinelRef.current;
        if (!root || !target) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0] && entries[0].isIntersecting) {
                    tryLoadMore();
                }
            },
            { root, rootMargin: '120px', threshold: 0 }
        );
        observer.observe(target);
        return () => observer.disconnect();
    }, [open, hasMore, loading, loadingMore, tryLoadMore, models && models.length]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <CustomSectionCreator isSubtle={true} title="Select Model" />
            <DialogContent
                ref={contentRef}
                sx={{ maxHeight: '70vh', overflowY: 'auto' }}
            >
                {showListLoading ? (
                    <Box display="flex" justifyContent="center" p={2}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                    <Box display="flex" justifyContent="center" p={2}>
                        <Button
                            onClick={handleAutoSelect}
                            variant="contained"
                            color="primary"
                            disabled={generating}
                            sx={{ mr: 1 }}
                        >
                            {generating ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                    Generating...
                                </>
                            ) : (
                                'Auto-Select Model'
                            )}
                        </Button>
                    </Box>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            OR select a specific model from below:
                        </Alert>
                        {typeof onGenderFilterChange === 'function' && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75 }}>
                                    Filter by gender
                                </Typography>
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={genderFilter ? genderFilter : 'all'}
                                    onChange={(e, val) => {
                                        if (val == null) return;
                                        onGenderFilterChange(val === 'all' ? '' : val);
                                    }}
                                    aria-label="Model gender filter"
                                    sx={{ flexWrap: 'wrap', gap: 0.5 }}
                                >
                                    <ToggleButton value="all">Top Models</ToggleButton>
                                    <ToggleButton value="female">Female/Women</ToggleButton>
                                    <ToggleButton value="male">Male/Men</ToggleButton>
                                    {/*<ToggleButton value="girl">Girl</ToggleButton>
                                    <ToggleButton value="boy">Boy</ToggleButton>*/}
                                </ToggleButtonGroup>
                            </Box>
                        )}
                        <Grid container spacing={2}>
                            {models && models.map((model) => {
                                const fullUrl = getFullImageUrl(model);
                                const thumbUrl = getThumbnailUrl(model);
                                const fallbackUrl =
                                    fullUrl ||
                                    model.image?.cdn?.secure_url ||
                                    model.image?.cdn?.url ||
                                    '';
                                return (
                                <Grid item xs={6} sm={4} key={String(model._id)}>
                                    <Card
                                        onClick={() => handleSelectModel(model)}
                                        sx={{
                                            cursor: 'pointer',
                                            border: selectedModelId === model._id ? '3px solid' : '2px solid',
                                            borderColor: selectedModelId === model._id ? 'primary.main' : 'grey.300',
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                borderColor: 'primary.light',
                                                transform: 'scale(1.02)',
                                            }
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                width: '100%',
                                                aspectRatio: '9 / 16',
                                                bgcolor: 'grey.200',
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={thumbUrl}
                                                alt={model.name}
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    display: 'block',
                                                }}
                                            />
                                            {/*thumbUrl && (
                                                <Box
                                                    component="img"
                                                    src={thumbUrl}
                                                    alt=""
                                                    aria-hidden
                                                    sx={{
                                                        position: 'absolute',
                                                        top: (theme) => theme.spacing(0.75),
                                                        right: (theme) => theme.spacing(0.75),
                                                        width: '30%',
                                                        height: 'auto',
                                                        maxWidth: '30%',
                                                        objectFit: 'cover',
                                                        borderRadius: 1,
                                                        boxShadow: 3,
                                                        border: (theme) =>
                                                            `1px solid ${theme.palette.background.paper}`,
                                                    }}
                                                />
                                            )*/}
                                        </Box>
                                    </Card>
                                </Grid>
                                );
                            })}
                        </Grid>
                        {hasMore && (
                            <Box ref={sentinelRef} display="flex" justifyContent="center" py={2} minHeight={48}>
                                {loadingMore ? <CircularProgress size={28} /> : null}
                            </Box>
                        )}
                        {(!models || models.length === 0) && !showListLoading && (
                            <Box textAlign="center" p={4}>
                                <Typography color="text.secondary">
                                    No models available
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit" disabled={generating}>
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color="primary"
                    disabled={!selectedModelId || generating}
                >
                    {generating ? (
                        <>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            Generating...
                        </>
                    ) : (
                        'Generate with Selected'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModelTryOnSelector;
